import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * MUI Components
 */
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import SelectAllIcon from '@mui/icons-material/SelectAll';

const WorkActionAreaControl = ({ list, value, handleChange, showParent = false, hideSectors = false }) => {
    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState(value);

    // Only set localValue from value on the first render
    useEffect(() => {
        setLocalValue(value);
        handleChange({ target: { name: 'selectActionArea', value } });
        // console.debug('WorkActionAreaControl local:', localValue, 'initial:', value);
    }, [value]);

    const handleLocalChange = (event) => {
        const newValue = event.target.value;
        setLocalValue(newValue);
        handleChange(event);
    };

    const findAreaWithID = (id) => {
        return list.find((area) => area.id === id);
    };

    const hasParent = (id) => {
        const item = findAreaWithID(id);
        if (item) {
            return item.parent_id > 0;
        }
        return false;
    };

    const getDisplayName = (id) => {
        const item = findAreaWithID(id);
        if (item) {
            if (showParent && hasParent(id)) {
                const parent = findAreaWithID(item.parent_id);
                if (parent) {
                    return `${parent.name} 🢒 ${item.name}`;
                }
            }

            return item.name;
        }

        console.warn('No item found for id:', id);
        return '?';
    };

    const getDisplayIcon = (id) => {
        const item = findAreaWithID(id);
        if (item) {
            if (hasParent(id)) {
                // Is a zone
                return (
                    <ListItemIcon>
                        <HighlightAltIcon fontSize="small" />
                    </ListItemIcon>
                );
            } else {
                // Is a sector
                return (
                    <ListItemIcon>
                        <SelectAllIcon fontSize="small" />
                    </ListItemIcon>
                );
            }
        }
    }

    const setMenuItemClass = (area) => {
        let isSector = !hasParent(area.id);
        let className = isSector ? 'parent' : 'child';

        if (hideSectors && isSector) {
            className += ' hidden';
        }

        return className;
    }

    return (
        <FormControl fullWidth>
            <InputLabel id="select-action-area-label">{t('Action area')}</InputLabel>
            <Select
                labelId="select-action-area-label"
                id="select-action-area"
                name="selectActionArea"
                value={localValue}
                onChange={handleLocalChange}
                label={t('Action area')}
                required
                disabled={list.length === 0} // Disable if no options
            >
                <MenuItem key={0} value={0}>
                    {t('No zone')}
                </MenuItem>
                {list.map((area) => (
                    <MenuItem
                        className={setMenuItemClass(area)}
                        key={area.id}
                        value={area.id}>
                        {getDisplayIcon(area.id)}
                        {getDisplayName(area.id)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

WorkActionAreaControl.propTypes = {
    value: PropTypes.number,
    state: PropTypes.array.isRequired,
    handleChange: PropTypes.func.isRequired,
    showParent: PropTypes.bool,
};

export default WorkActionAreaControl;
